/* eslint-disable react/no-unknown-property */
/* eslint-disable react/jsx-props-no-spreading */
import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Script from 'next/script';
import { withTranslateRoutes } from 'next-translate-routes';
import { useRouter } from 'next/router';

import { Oswald, Lato } from '@next/font/google';

// Libs
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnimatePresence } from 'framer-motion';
import { appWithTranslation } from 'next-i18next';
import packageJson from '../package.json';

// Utils
import { AppProvider } from '../src/store/AppContext';
import { COOKIE_VERSIONS } from '../src/constants';

// Components
import PageLoader from '../components/templates/PageTransition/PageLoader/PageLoader';

// Styles
import '../styles/main.css';

// Fonts
// eslint-disable-next-line no-unused-vars
const oswald = Oswald({
  weight: ['300', '500', '700'],
  style: ['normal'],
  subsets: ['latin'],
  variable: '--font-oswald',
});
// eslint-disable-next-line no-unused-vars
const lato = Lato({
  weight: ['300', '400', '900'],
  style: ['normal'],
  subsets: ['latin'],
  variable: '--font-lato',
});

function App({ Component, pageProps }) {
  // VERSION NUMBER IN CONSOLE
  console.log(`Build version: ${packageJson.version}`);

  const { pathname, events } = useRouter();

  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  }));

  const isBookingTunnel = useMemo(() => pathname.startsWith('/booking/'), [pathname]);

  // Effects
  useEffect(() => {
    const handleRouteChange = (url) => {
      if (window.dataLayer && process.env.NEXT_PUBLIC_ENV === 'production') {
        window.dataLayer.push({
          event: 'page_view',
          page: url,
        });
      }
    };

    events.on('routeChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [events]);

  return (
    <AppProvider>
      <Head>
        <title>Student Factory</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" crossOrigin="use-credentials" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />

        <link rel="preconnect" href="https://www.googletagmanager.com" />
      </Head>

      <style jsx global>
        {`
        :root {
          --oswald-font: ${oswald.style.fontFamily};
          --lato-font: ${lato.style.fontFamily};
        }
      `}
      </style>

      {process.env.NEXT_PUBLIC_AXEPTIO_CLIENTID ? (
        <Script
          id="axeptio-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            window.axeptioSettings = {
              clientId: '${process.env.NEXT_PUBLIC_AXEPTIO_CLIENTID}',
              cookiesVersion: '${COOKIE_VERSIONS[pageProps.locale]}',
            };
          `,
          }}
        />
      ) : null}

      {/* Google Tag Manager */}
      {process.env.NEXT_PUBLIC_GTM_ID ? (
        <Script
          id="gtm-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID}');
                `,
          }}
        />
      ) : null}
      {/* End Google Tag Manager */}

      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <AnimatePresence mode="wait">
            <Component {...pageProps} />
          </AnimatePresence>
        </Hydrate>
        <ReactQueryDevtools />
      </QueryClientProvider>

      {isBookingTunnel ? <PageLoader /> : null}

      {/* Google Tag Manager */}
      {process.env.NEXT_PUBLIC_GTM_ID ? (
        <noscript>
          <iframe
            title="gtm"
            src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      ) : null}
      {/* End Google Tag Manager */}

    </AppProvider>
  );
}

App.propTypes = {
  Component: PropTypes.func.isRequired,
  pageProps: PropTypes.shape({
    dehydratedState: PropTypes.shape({}),
    locale: PropTypes.string,
  }).isRequired,
};

export default withTranslateRoutes(JSON.parse(`{"defaultLocale":"default","locales":["default","fr","en"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"index","paths":{"default":"index"}},{"name":"sitemap.xml","paths":{"default":"sitemap.xml"}},{"name":"[slug]","paths":{"default":":slug"}},{"name":"apart-hotel","paths":{"default":"apart-hotel","fr":"appart-hotel","en":"apart-hotel"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"booking","paths":{"default":"booking"},"children":[{"name":"complete","paths":{"default":"complete"}},{"name":"customization","paths":{"default":"customization"}},{"name":"dates","paths":{"default":"dates"}},{"name":"formula","paths":{"default":"formula"}},{"name":"guarantors","paths":{"default":"guarantors"}},{"name":"incomplete","paths":{"default":"incomplete"}},{"name":"saved","paths":{"default":"saved"}},{"name":"tenant","paths":{"default":"tenant"}},{"name":"error","paths":{"default":"error"},"children":[{"name":"invalid","paths":{"default":"invalid"}},{"name":"not-found","paths":{"default":"not-found"}}]}]},{"name":"conseils-et-actus","paths":{"default":"conseils-et-actus"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[category]","paths":{"default":":category"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]}]},{"name":"contact","paths":{"default":"contact"},"children":[{"name":"call","paths":{"default":"call","fr":"call-merci","en":"call-thank-you"}},{"name":"index","paths":{"default":"index"}},{"name":"mail","paths":{"default":"mail","fr":"mail-merci","en":"mail-thank-you"}}]},{"name":"invest","paths":{"default":"invest","fr":"investir-en-residence-etudiante","en":"invest"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"our-student-residences","paths":{"default":"our-student-residences","fr":"nos-residences-etudiantes","en":"our-student-residences"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"site-map","paths":{"default":"site-map","fr":"plan-du-site","en":"site-map"},"children":[{"name":"index","paths":{"default":"index"}}]},{"name":"student-residences","paths":{"default":"student-residences","fr":"residences-etudiantes","en":"student-residences"},"children":[{"name":"[slug]","paths":{"default":":slug"}}]}]},"fallbackLng":{}}`), appWithTranslation(App));
